import { useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { GlanceCard } from '../shell/GlanceCard';
import { AlertRow } from '../shell/AlertRow';
import { ConfirmModal } from '../ConfirmModal';
import { Spinner } from '../Spinner';
import { ErrorMessage } from '../ErrorMessage';
import { DRAGON_TYPES, stateBg, stateFg, inputStyle, primaryButtonStyle, secondaryButtonStyle, dangerButtonStyle } from './dragonPresentation';
import { DRAGON_MAX_CONTRIBUTION_PCT, DRAGON_SOLDIER_HP_DAMAGE, DRAGON_SPECIALIST_HP_DAMAGE } from '../../constants/dragon';
import type { DragonsShellProps } from '../../pages/Dragons';
import type { DragonType, DragonSendMode } from '../../types/dragon';

const mobileInputStyle = { ...inputStyle, minHeight: 44 };

/**
 * SF-153: mobile card-based content for Dragons — 10 cards/sections gated behind the page's
 * role/state conditions (Codebase Context). No sticky bottom bar — each card's action is a single
 * independent decision point, not an accumulating multi-row order (matches SF-139/149).
 */
export function MobileDragonsShell({
  status,
  isLoading,
  error,
  busyAction,
  isMonarch,
  hasActive,
  callerIsAttacker,
  callerIsTarget,
  fundingProgress,
  availableGold,
  atHomeSoldiers,
  atHomeSpec,
  ticksRemainingText,
  myProvinceId,
  targetKingdomNumber,
  setTargetKingdomNumber,
  targetIslandNumber,
  setTargetIslandNumber,
  dragonType,
  setDragonType,
  sendMode,
  setSendMode,
  fundAmount,
  setFundAmount,
  slaySoldiers,
  setSlaySoldiers,
  slaySpecs,
  setSlaySpecs,
  contributionCapRemaining,
  maxUsefulSoldiers,
  maxUsefulSpecialists,
  mutationVersion,
  showCancelConfirm,
  setShowCancelConfirm,
  showSlayConfirm,
  setShowSlayConfirm,
  slayRequiresTypedConfirm,
  handleStart,
  handleContribute,
  handleMaxContribute,
  handleSend,
  handleToggleMode,
  handleCancelRequest,
  handleCancelConfirm,
  handleSlayRequest,
  handleSlayConfirm,
  handleMaxSoldiers,
  handleMaxSpecs,
}: DragonsShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  useEffect(() => {
    if (mutationVersion > 0) {
      h1Ref.current?.focus();
    }
  }, [mutationVersion]);

  const introDisclosure = (
    <details className="text-sm rounded-lg p-3" style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, color: COLORS.text.tertiary }}>
      <summary style={{ cursor: 'pointer', color: COLORS.text.secondary }}>What are dragon projects?</summary>
      <p style={{ margin: '8px 0 6px' }}>
        The <span style={{ color: COLORS.gold }}>Monarch</span> may rally the kingdom to send a dragon against an enemy.
        Members pool gold to fund it; once launched, the dragon ravages the target for six Utopian months, applying kingdom-wide debuffs.
      </p>
      <p style={{ margin: 0 }}>
        Funded dragons can be launched automatically once funding completes, or held for a manual launch.
        The target kingdom may sacrifice soldiers and specialists to slay the dragon before it expires.
      </p>
    </details>
  );

  const contributionsCount = status?.contributions?.length ?? 0;
  const showContributionsCard = callerIsAttacker && contributionsCount > 0;

  const contributeCapExhausted = contributionCapRemaining <= 0;
  // SF-153 Critical Issue 11: each mutating button's disabled/aria-busy state derives from
  // `busyAction === '<its own key>'`, not a blanket `busyAction !== null` — a shared boolean
  // can't tell a screen reader which of up to three stacked buttons is actually in flight. The
  // page-level `wrap()` guard clause (`if (busyAction !== null) return;`) still serializes.
  const startBusy = busyAction === 'start';
  const contributeBusy = busyAction === 'contribute';
  const sendBusy = busyAction === 'send';
  const toggleModeBusy = busyAction === 'toggleMode';
  const cancelBusy = busyAction === 'cancel';
  const slayBusy = busyAction === 'slay';
  const attackDisabled = slayBusy || (Number(slaySoldiers || 0) <= 0 && Number(slaySpecs || 0) <= 0);

  return (
    <div className="space-y-3">
      <h1
        ref={h1Ref}
        tabIndex={-1}
        className="outline-none"
        style={{ margin: 0, color: COLORS.text.primary, fontSize: 18, fontWeight: 700 }}
      >
        Dragons
      </h1>

      {isLoading && !status && <Spinner />}
      {error && !status && <ErrorMessage message={error} />}

      {status && (
        <>
          {status.hasProject && (
            <GlanceCard title="Current Project">
              <div className="flex items-center justify-between">
                <strong style={{ color: COLORS.text.primary }}>
                  {formatEnumLabel(status.dragonType ?? '')} Dragon
                </strong>
                <span style={{
                  padding: '2px 8px', borderRadius: 4, fontSize: 11,
                  background: stateBg(status.state), color: stateFg(status.state),
                  border: `1px solid ${stateFg(status.state)}`,
                }}>
                  {formatEnumLabel(status.state ?? '')}
                </span>
              </div>

              {/* Critical Issue 2: keyed off callerIsTarget (matching the enemy-row's discriminant
                  below), copying KingdomHub.tsx:66-70's exact fallback-direction precedent —
                  `role === null` is defensive only; `=== 'TARGET'` naturally evaluates false for
                  null, so an unset role falls through to the non-target/attacker branch: the
                  safer failure direction, since it never overstates threat. */}
              <div style={{ fontSize: 12, color: callerIsTarget ? COLORS.warning : COLORS.text.tertiary }}>
                {callerIsTarget
                  ? '🎯 Your kingdom is the target — fight back below.'
                  : '⚔️ Your kingdom started this project.'}
              </div>

              <div className="flex flex-col gap-1.5" style={{ fontSize: 13 }}>
                <div>
                  <span style={{ color: COLORS.text.tertiary }}>Enemy kingdom: </span>
                  <Link
                    to={`/world/kingdoms/${callerIsTarget ? status.attackerKingdomId : status.targetKingdomId}`}
                    style={{ color: COLORS.link }}
                  >
                    {callerIsTarget ? status.attackerKingdomId : status.targetKingdomId}
                  </Link>
                </div>
                {status.sendMode && (
                  <div>
                    <span style={{ color: COLORS.text.tertiary }}>Send mode: </span>
                    <span style={{ color: COLORS.text.secondary }}>
                      {formatEnumLabel(status.sendMode)} {status.modeLocked ? '(locked)' : ''}
                    </span>
                  </div>
                )}
                {status.hp !== null && (
                  <div>
                    <span style={{ color: COLORS.text.tertiary }}>HP: </span>
                    <span style={{ color: COLORS.text.secondary }}>{formatNumber(status.hp)}</span>
                  </div>
                )}
                {status.expiresAtTick !== null && (
                  <div>
                    <span style={{ color: COLORS.text.tertiary }}>Expires in: </span>
                    <span style={{ color: COLORS.text.secondary }}>{ticksRemainingText}</span>
                  </div>
                )}
              </div>

              {status.totalCost != null && status.totalCost > 0 && (
                <div style={{ marginTop: 4 }}>
                  <div className="flex items-center justify-between" style={{ fontSize: 12, color: COLORS.text.tertiary, marginBottom: 4 }}>
                    <span>Funding</span>
                    <span style={{ color: COLORS.text.secondary }}>
                      {formatNumber(status.amountFunded ?? 0)} / {formatNumber(status.totalCost)} gc
                    </span>
                  </div>
                  <div
                    role="progressbar"
                    aria-valuenow={Math.round(fundingProgress)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Funding progress"
                    style={{ height: 8, background: COLORS.cardAlt, border: `1px solid ${COLORS.borderInset}`, borderRadius: 4, overflow: 'hidden' }}
                  >
                    <div style={{ height: '100%', width: `${fundingProgress}%`, background: COLORS.gold, transition: 'width 0.3s' }} />
                  </div>
                </div>
              )}
            </GlanceCard>
          )}

          {showContributionsCard && (
            <GlanceCard title="Contributions" badge={contributionsCount}>
              <div className="flex flex-col gap-1.5">
                {status.contributions!.map(c => (
                  <div key={c.provinceId} className="flex items-center justify-between gap-2" style={{ fontSize: 12 }}>
                    <span
                      title={c.provinceId}
                      style={{ color: COLORS.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                    >
                      {c.provinceId}{c.provinceId === myProvinceId ? ' (You)' : ''}
                    </span>
                    <span style={{ color: COLORS.text.secondary, flexShrink: 0 }}>{formatNumber(c.amount)}</span>
                  </div>
                ))}
              </div>
            </GlanceCard>
          )}

          {introDisclosure}

          {isMonarch && !hasActive && (
            <GlanceCard title="Start a Dragon Project">
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <label className="flex-1" style={{ fontSize: 12, color: COLORS.text.tertiary }}>
                    Kingdom
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={targetKingdomNumber}
                      onChange={e => setTargetKingdomNumber(e.target.value)}
                      onFocus={e => e.target.select()}
                      style={mobileInputStyle}
                    />
                  </label>
                  <label className="flex-1" style={{ fontSize: 12, color: COLORS.text.tertiary }}>
                    Island
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={targetIslandNumber}
                      onChange={e => setTargetIslandNumber(e.target.value)}
                      onFocus={e => e.target.select()}
                      style={mobileInputStyle}
                    />
                  </label>
                </div>
                <label style={{ fontSize: 12, color: COLORS.text.tertiary }}>
                  Dragon type
                  <select value={dragonType} onChange={e => setDragonType(e.target.value as DragonType)} style={mobileInputStyle}>
                    {DRAGON_TYPES.map(d => (
                      <option key={d.value} value={d.value}>{d.label} — {d.effect}</option>
                    ))}
                  </select>
                </label>
                <label style={{ fontSize: 12, color: COLORS.text.tertiary }}>
                  Send mode
                  <select value={sendMode} onChange={e => setSendMode(e.target.value as DragonSendMode)} style={mobileInputStyle}>
                    <option value="AUTO_ON_FUNDED">Auto — launch on funding complete</option>
                    <option value="MANUAL">Manual — launch on your command</option>
                  </select>
                  <div style={{ fontSize: 11, color: COLORS.text.tertiary, marginTop: 2 }}>
                    Locks in after your kingdom's first contribution — choose carefully
                  </div>
                </label>
              </div>
              <button
                disabled={startBusy || !targetKingdomNumber || !targetIslandNumber}
                aria-busy={startBusy}
                onClick={handleStart}
                style={{ ...primaryButtonStyle(startBusy || !targetKingdomNumber || !targetIslandNumber), minHeight: 44, width: '100%' }}
              >
                Start Project
              </button>
            </GlanceCard>
          )}

          {callerIsAttacker && status.state === 'FUNDING' && (
            <GlanceCard title="Contribute Gold">
              <div style={{ fontSize: 12, color: COLORS.text.tertiary }}>
                Your gold: <span style={{ color: COLORS.gold }}>{formatNumber(availableGold)}</span>.
                A single province may contribute at most {DRAGON_MAX_CONTRIBUTION_PCT * 100}% of the total cost.
                {' '}Your remaining headroom: <strong>{formatNumber(contributionCapRemaining)}</strong> gc.
              </div>
              {contributeCapExhausted && (
                <div style={{ fontSize: 12, color: COLORS.warning }}>
                  You've already contributed the maximum ({DRAGON_MAX_CONTRIBUTION_PCT * 100}% of total cost) allowed per province.
                </div>
              )}
              <div className="flex flex-col gap-2">
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={fundAmount}
                  onChange={e => !contributeCapExhausted && setFundAmount(e.target.value)}
                  onFocus={e => e.target.select()}
                  placeholder="Amount"
                  aria-label="Contribution amount"
                  aria-disabled={contributeCapExhausted}
                  style={mobileInputStyle}
                />
                <div className="flex gap-2">
                  <button
                    aria-label="Max contribution"
                    aria-disabled={contributeCapExhausted}
                    onClick={() => !contributeCapExhausted && handleMaxContribute()}
                    style={{ ...secondaryButtonStyle(contributeCapExhausted), minHeight: 44, flex: 1 }}
                  >
                    Max
                  </button>
                  <button
                    aria-disabled={contributeCapExhausted || undefined}
                    aria-busy={contributeBusy}
                    disabled={!contributeCapExhausted && (contributeBusy || !fundAmount || Number(fundAmount) <= 0)}
                    onClick={() => { if (contributeCapExhausted) return; handleContribute(); }}
                    style={{ ...primaryButtonStyle(contributeCapExhausted || contributeBusy || !fundAmount || Number(fundAmount) <= 0), minHeight: 44, flex: 1, marginTop: 0 }}
                  >
                    Contribute
                  </button>
                </div>
              </div>
            </GlanceCard>
          )}

          {isMonarch && callerIsAttacker && status.state && ['FUNDING', 'FUNDED_READY'].includes(status.state) && (
            <GlanceCard title="Monarch Controls">
              <div className="flex flex-col gap-2">
                {status.state === 'FUNDED_READY' && (
                  <button
                    disabled={sendBusy}
                    aria-busy={sendBusy}
                    onClick={handleSend}
                    style={{ ...primaryButtonStyle(sendBusy), minHeight: 44, width: '100%', marginTop: 0 }}
                  >
                    Send Dragon
                  </button>
                )}
                {!status.modeLocked && (
                  <button
                    disabled={toggleModeBusy}
                    aria-busy={toggleModeBusy}
                    onClick={handleToggleMode}
                    style={{ ...secondaryButtonStyle(toggleModeBusy), minHeight: 44, width: '100%' }}
                  >
                    Toggle Send Mode
                  </button>
                )}
                <button
                  disabled={cancelBusy}
                  aria-busy={cancelBusy}
                  onClick={handleCancelRequest}
                  style={{ ...dangerButtonStyle(cancelBusy), minHeight: 44, width: '100%' }}
                >
                  Cancel Project
                </button>
              </div>
            </GlanceCard>
          )}

          {callerIsTarget && status.state === 'ACTIVE' && (
            <GlanceCard title="Slay the Dragon">
              <div style={{ fontSize: 12, color: COLORS.text.tertiary }}>
                Each soldier deals {DRAGON_SOLDIER_HP_DAMAGE} HP; each specialist deals {DRAGON_SPECIALIST_HP_DAMAGE} HP. All units sent are lost, whether or not the dragon is slain.
                At home: <strong>{formatNumber(atHomeSoldiers)}</strong> soldiers, <strong>{formatNumber(atHomeSpec)}</strong> specialists.
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={slaySoldiers}
                    onChange={e => setSlaySoldiers(e.target.value)}
                    onFocus={e => e.target.select()}
                    placeholder="Soldiers"
                    aria-label="Soldiers to send"
                    style={{ ...mobileInputStyle, flex: 1 }}
                  />
                  <button
                    aria-label="Max soldiers"
                    aria-disabled={maxUsefulSoldiers <= 0}
                    onClick={() => maxUsefulSoldiers > 0 && handleMaxSoldiers()}
                    style={{ ...secondaryButtonStyle(maxUsefulSoldiers <= 0), minHeight: 44 }}
                  >
                    Max
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={slaySpecs}
                    onChange={e => setSlaySpecs(e.target.value)}
                    onFocus={e => e.target.select()}
                    placeholder="Specialists"
                    aria-label="Specialists to send"
                    style={{ ...mobileInputStyle, flex: 1 }}
                  />
                  <button
                    aria-label="Max specialists"
                    aria-disabled={maxUsefulSpecialists <= 0}
                    onClick={() => maxUsefulSpecialists > 0 && handleMaxSpecs()}
                    style={{ ...secondaryButtonStyle(maxUsefulSpecialists <= 0), minHeight: 44 }}
                  >
                    Max
                  </button>
                </div>
                <button
                  disabled={attackDisabled}
                  aria-busy={slayBusy}
                  onClick={handleSlayRequest}
                  style={{ ...dangerButtonStyle(attackDisabled), minHeight: 44, width: '100%' }}
                >
                  Attack
                </button>
              </div>
            </GlanceCard>
          )}

          {!status.hasProject && !isMonarch && (
            <AlertRow severity="info" message="No active dragon project. Only the Monarch may initiate one." />
          )}
        </>
      )}

      <ConfirmModal
        isOpen={showCancelConfirm}
        onConfirm={handleCancelConfirm}
        onCancel={() => setShowCancelConfirm(false)}
        title="Cancel Project"
      >
        <p>Cancel the project? Contributed gold is NOT refunded.</p>
      </ConfirmModal>

      <ConfirmModal
        isOpen={showSlayConfirm}
        onConfirm={handleSlayConfirm}
        onCancel={() => setShowSlayConfirm(false)}
        title="Slay the Dragon"
        confirmText="Attack"
        requireTypedConfirmation={slayRequiresTypedConfirm ? { keyword: 'SLAY' } : undefined}
      >
        <p>
          Sacrifice {formatNumber(Number(slaySoldiers || 0))} soldiers and {formatNumber(Number(slaySpecs || 0))} specialists?
          They will be permanently lost.
        </p>
      </ConfirmModal>
    </div>
  );
}
